import React, { useState, useEffect } from 'react';
import GenericMap from '../../../shared/components/common/GenericMap.jsx';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';
import {
  ArrowClockwise,
  Car,
  Warning,
  Lightning,
  BatteryMedium,
  Speedometer,
  MapPin,
  Funnel,
  X,
  FlyingSaucer,
  CarProfileIcon,
  Cow,
  HouseIcon
} from '@phosphor-icons/react';
import { AuthContext } from '../../../context/authContext.jsx';
import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { TASK_STATUS_CONFIG, getTaskStatusColor } from '../../../constants/taskColors.js';
import { teamAssignmentService } from '../services/teamAssignmentService.js';
import { teamService } from '../../../services/teams/teamService.js';
import { teamTaskService } from '../services/teamTaskService.js';
import { teamVehicleService } from '../services/teamVehicleService.js';

// Icono de vehículo para el mapa (usando Phosphor)
const vehicleIcon = L.divIcon({
  html: renderToStaticMarkup(
    <div style={{
      backgroundColor: 'var(--amber)',
      borderRadius: '50%',
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '3px solid white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
    }}>
      <CarProfileIcon size={20} weight="fill" color="purple" />
    </div>
  ),
  className: 'custom-vehicle-icon',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

// Función para crear icono de tarea según estado (usando Phosphor)
const createTaskIcon = (status) => {
  const config = TASK_STATUS_CONFIG[status] || TASK_STATUS_CONFIG.pendiente;
  return L.divIcon({
    html: renderToStaticMarkup(
      <div style={{
        backgroundColor: config.color,
        borderRadius: '50% 50% 50% 0',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '3px solid white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        transform: 'rotate(-45deg)'
      }}>
        <div style={{ transform: 'rotate(45deg)' }}>
          <HouseIcon size={18} weight="fill" color="white" />
        </div>
      </div>
    ),
    className: 'custom-task-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const EquiposPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({ teamId: '', taskStatuses: [] });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [mapCenter, setMapCenter] = useState([-31.5375, -68.5364]); // San Juan, Argentina
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext)

  // Función para detectar el rol del usuario
  const detectUserRole = () => {
    const token = Cookies.get('sessionToken');
    if (!token) return 'admin';

    try {
      const decoded = jwtDecode(token);
      return decoded.role || 'admin';
    } catch (error) {
      console.error('Error decodificando token:', error);
      return 'admin';
    }
  };

  // Función para obtener vehículos, equipos, asignaciones y tareas
  const fetchVehicles = async () => {
    try {
      setError(null);

      const [vehiclesData, teamsData, assignmentsData, tasksData] = await Promise.all([
        teamVehicleService.getVehiclesWithLocation(),
        teamService.getTeams(),
        teamAssignmentService.getAssignments(),
        teamTaskService.getTodayTasksWithLocation()
      ]);

      // Procesar vehículos
      const validVehicles = Array.isArray(vehiclesData) ? vehiclesData : [];
      setVehicles(validVehicles);
      setLastUpdate(new Date());

      if (validVehicles.length > 0) {
        const avgLat = validVehicles.reduce((sum, v) => sum + parseFloat(v.latitude), 0) / validVehicles.length;
        const avgLng = validVehicles.reduce((sum, v) => sum + parseFloat(v.longitude), 0) / validVehicles.length;
        setMapCenter([avgLat, avgLng]);
      }

      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);

      const validStatuses = ['pendiente', 'en_camino', 'iniciada', 'relevada', 'finalizada', 'cancelada'];
      const validTasks = Array.isArray(tasksData)
        ? tasksData.filter((task) => validStatuses.includes(task.status))
        : [];
      setTasks(validTasks);
      return;

    } catch (err) {
      console.error('❌ Error obteniendo datos:', err);
      setError('Error al cargar los datos');
      setVehicles([]);
      setTeams([]);
      setAssignments([]);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener el estado del vehículo
  const getVehicleStatus = (vehicle) => {
    if (vehicle.status?.inMotion) return 'En movimiento';
    if (vehicle.status?.contactOn) return 'Contacto encendido';
    if (vehicle.status?.mainPowerOn) return 'Energía principal encendida';
    return 'Detenido';
  };

  // Función para obtener el color del estado
  const getStatusColor = (vehicle) => {
    if (vehicle.status?.inMotion) return 'text-success bg-success/12';
    if (vehicle.status?.contactOn) return 'text-info bg-info/12';
    if (vehicle.status?.mainPowerOn) return 'text-warning bg-warning/12';
    return 'text-ink bg-[rgb(var(--paper-line-rgb))]';
  };

  // Cargar datos iniciales
  useEffect(() => {
    setUserRole(detectUserRole());
    fetchVehicles();
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="container">
        <Navigate to="/admin/login" replace />
      </div>
    )
  }
  return (
    <div className="">
      <PageHeader
        title="Flota de Vehículos"
        subtitle="Monitoreo en tiempo real de la flota de vehículos de ONPOWER"
        icon={<Car size={28} />}
        actions={(
          <button
            type="button"
            onClick={fetchVehicles}
            disabled={loading}
            className="bg-surface-2 hover:bg-line disabled:bg-muted-deep text-content px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors"
          >
            {loading ? (
              <>
                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-content" />
                Actualizando...
              </>
            ) : (
              <>
                <ArrowClockwise size={20} />
                Actualizar Datos
              </>
            )}
          </button>
        )}
      />

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-line">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-muted">Total de Vehículos</p>
              <p className="text-3xl font-bold text-content">{vehicles.length}</p>
            </div>
            <Car size={32} className="text-content-muted" />
          </div>
        </div>
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-line">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-muted">En Movimiento</p>
              <p className="text-3xl font-bold text-content">
                {vehicles.filter(v => v.status?.inMotion).length}
              </p>
            </div>
            <Car size={32} className="text-content-muted" />
          </div>
        </div>
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-line">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-muted">Con Contacto</p>
              <p className="text-3xl font-bold text-content">
                {vehicles.filter(v => v.status?.contactOn).length}
              </p>
            </div>
            <Warning size={32} className="text-content-muted" />
          </div>
        </div>
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-line">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-content-muted">Última Actualización</p>
              <p className="text-lg font-bold text-content">
                {lastUpdate.toLocaleTimeString()}
              </p>
            </div>
            <Lightning size={32} className="text-content-muted" />
          </div>
        </div>
      </div>

      {/* Mapa Full Width con FAB */}
      <div className="bg-surface rounded-xl shadow-lg border border-line">
        <div className="px-6 py-4 border-b border-line">
          <h2 className="text-lg font-semibold text-content">Mapa de flotas y tareas del día</h2>
        </div>

        <div className="p-6">
          <div className="h-[600px] w-full rounded-lg overflow-hidden border border-line relative">
            {/* Floating Action Button */}
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className="absolute top-4 right-4 z-[1000] bg-surface hover:bg-surface-2 text-content rounded-full p-2 shadow-lg transition-all duration-200 flex items-center gap-2"
              title="Filtros"
            >
              <Funnel size={16} weight="fill" />
            </button>

            {/* Side Navigation Panel */}
            <div
              className={`absolute top-0 right-0 h-full w-48 bg-surface shadow-2xl z-[999] transform transition-transform duration-300 ease-in-out ${showFilterPanel ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-line flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-content flex items-center gap-2">
                    Filtros
                  </h3>
                </div>

                {/* Filters Content */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                  {/* Filtro por Equipo */}
                  <div>
                    <label className="block text-sm font-medium text-content-muted mb-2 text-xs">
                      Equipo
                    </label>
                    <select
                      value={filters.teamId}
                      onChange={(e) => {
                        const newTeamId = e.target.value;
                        setFilters({ ...filters, teamId: newTeamId });
                        // Centrar mapa en vehículo del equipo
                        if (newTeamId) {
                          const activeAssignment = assignments.find(a =>
                            a.teamId === newTeamId &&
                            (a.isActive === true || a.isActive === undefined)
                          );
                          const assignedVehicle = activeAssignment
                            ? vehicles.find(v => v.id === activeAssignment.vehicleId)
                            : null;
                          if (assignedVehicle) {
                            setMapCenter([
                              parseFloat(assignedVehicle.latitude),
                              parseFloat(assignedVehicle.longitude)
                            ]);
                          }
                        }
                      }}
                      className="w-full px-2 py-1 bg-surface-2 border border-line rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-amber text-xs"
                    >
                      <option value="">Todos los equipos</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por Estados de Tareas */}
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">
                      Estados de Tareas
                    </label>
                    <div className="space-y-2">
                      {Object.entries(TASK_STATUS_CONFIG).map(([status, config]) => {
                        const isSelected = filters.taskStatuses.includes(status);
                        return (
                          <div
                            key={status}
                            onClick={() => {
                              const newStatuses = isSelected
                                ? filters.taskStatuses.filter(s => s !== status)
                                : [...filters.taskStatuses, status];
                              setFilters({ ...filters, taskStatuses: newStatuses });
                            }}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all text-sm ${isSelected
                              ? 'bg-surface-2 border-2 border-muted-deep'
                              : 'bg-transparent border-2 border-transparent hover:bg-surface-2'
                              }`}
                          >
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: config.color }}
                            ></div>
                            <span className="text-sm text-content-muted">
                              {config.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Footer con botones */}
                <div className="px-3 py-4 border-t border-line space-y-2">
                  <button
                    onClick={() => {
                      setFilters({ teamId: '', taskStatuses: [] });
                    }}
                    className="w-full px-2 py-1 bg-surface-2 hover:bg-line text-content rounded-lg transition-colors text-sm"
                  >
                    Limpiar Filtros
                  </button>
                  <button
                    onClick={() => setShowFilterPanel(false)}
                    className="w-full px-2 py-1 bg-amber hover:bg-amber-hover text-on-amber rounded-lg transition-colors text-sm"
                  >
                    Aplicar
                  </button>
                </div>
              </div>
            </div>
            <GenericMap
              center={mapCenter}
              zoom={13}
              loading={loading}
              minZoom={11}
              maxZoom={17}
              style={{ height: '100%', width: '100%', position: 'sticky' }}
              markers={[
                ...vehicles
                  .filter(vehicle => {
                    // Si hay un equipo seleccionado, filtrar por vehículo asignado
                    if (filters.teamId) {
                      const activeAssignment = assignments.find(a =>
                        a.teamId === filters.teamId &&
                        (a.isActive === true || a.isActive === undefined)
                      );
                      // Solo mostrar el vehículo asignado a este equipo
                      return activeAssignment && vehicle.id === activeAssignment.vehicleId;
                    }
                    // Si no hay filtro, mostrar todos los vehículos
                    return true;
                  })
                  .map((vehicle, index) => ({
                    id: `vehicle-${vehicle.id || index}`,
                    position: [parseFloat(vehicle.latitude), parseFloat(vehicle.longitude)],
                    icon: vehicleIcon,
                    popup: (
                      <div className="p-2">
                        <h4 className="font-semibold text-sm">🚗 {vehicle.name} - {vehicle.model}</h4>
                        <p className="text-xs text-ink">{vehicle.make} {vehicle.year}</p>
                        <p className="text-xs text-ink">Velocidad: {vehicle.speed || 0} km/h</p>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(vehicle)}`}>
                          {getVehicleStatus(vehicle)}
                        </span>
                      </div>
                    )
                  })),
                ...tasks
                  .filter(task => {
                    // Filtrar por equipo
                    if (filters.teamId && task.teamId !== filters.teamId) {
                      return false;
                    }
                    // Filtrar por estados seleccionados
                    if (filters.taskStatuses.length > 0 && !filters.taskStatuses.includes(task.status)) {
                      return false;
                    }
                    return true;
                  })
                  .map((task, index) => ({
                    id: `task-${task.id || index}`,
                    position: [parseFloat(task.client.lat), parseFloat(task.client.lng)],
                    icon: createTaskIcon(task.status),
                    popup: (
                      <div className="p-2">
                        <h4 className="font-semibold text-sm mb-2">{task.title}</h4>
                        <p className="text-xs text-ink"><strong>Cliente:</strong> {task.client?.name}</p>
                        <p className="text-xs text-ink"><strong>Teléfono:</strong> {task.client?.phone}</p>
                      </div>
                    )
                  }))
              ]}
            />
          </div>

          {/* Leyenda de estados */}
          <div className="pb-3 pt-3 ">
            <h3 className="text-sm font-semibold text-content mb-3">Leyenda de Estados</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(TASK_STATUS_CONFIG).map(([status, config]) => (
                <div key={status} className="flex items-center gap-1">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: config.color }}
                  ></div>
                  <span className="text-xs text-content-muted">
                    {config.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de detalles del vehículo */}
      {selectedVehicle && (
        <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-9998 p-4">
          <div className="bg-surface rounded-xl shadow-xl border border-line w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="bg-surface shadow-sm text-content px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Car size={20} />
                  Detalles del Vehículo
                </h3>
                <button
                  onClick={() => setSelectedVehicle(null)}
                  className="text-content hover:text-content-muted transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Información básica */}
                <div className="bg-surface-2 p-4 rounded-lg">
                  <h4 className="font-semibold text-content mb-2">Información Básica</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-content-muted">
                    <div><strong>Nombre:</strong> {selectedVehicle.name}</div>
                    <div><strong>Modelo:</strong> {selectedVehicle.model}</div>
                    <div><strong>Marca:</strong> {selectedVehicle.make}</div>
                    <div><strong>Año:</strong> {selectedVehicle.year}</div>
                    <div><strong>Color:</strong> {selectedVehicle.color}</div>
                    <div><strong>Placa:</strong> {selectedVehicle.plate || 'N/A'}</div>
                  </div>
                </div>

                {/* Ubicación */}
                <div className="bg-surface-2 p-4 rounded-lg">
                  <h4 className="font-semibold text-content mb-2">Ubicación Actual</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-content-muted">
                    <div><strong>Latitud:</strong> {selectedVehicle.latitude}</div>
                    <div><strong>Longitud:</strong> {selectedVehicle.longitude}</div>
                    <div><strong>Altitud:</strong> {selectedVehicle.altitude || 'N/A'} m</div>
                    <div><strong>Velocidad:</strong> {selectedVehicle.speed || 0} km/h</div>
                    <div><strong>Dirección:</strong> {selectedVehicle.heading || 0}°</div>
                    <div><strong>Última señal:</strong> {selectedVehicle.lastSignal ? new Date(selectedVehicle.lastSignal).toLocaleString() : 'N/A'}</div>
                  </div>
                </div>

                {/* Estado del vehículo */}
                <div className="bg-surface-2 p-4 rounded-lg">
                  <h4 className="font-semibold text-content mb-2">Estado del Vehículo</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-content-muted">
                    <div><strong>En movimiento:</strong>
                      <span className={`ml-1 px-2 py-1 rounded text-xs ${selectedVehicle.status?.inMotion
                        ? 'bg-success/12 text-success'
                        : 'bg-surface-2 text-content-muted'
                        }`}>
                        {selectedVehicle.status?.inMotion ? 'Sí' : 'No'}
                      </span>
                    </div>
                    <div><strong>Contacto:</strong>
                      <span className={`ml-1 px-2 py-1 rounded text-xs ${selectedVehicle.status?.contactOn
                        ? 'bg-success/12 text-success'
                        : 'bg-surface-2 text-content-muted'
                        }`}>
                        {selectedVehicle.status?.contactOn ? 'Encendido' : 'Apagado'}
                      </span>
                    </div>
                    <div><strong>Energía principal:</strong>
                      <span className={`ml-1 px-2 py-1 rounded text-xs ${selectedVehicle.status?.mainPowerOn
                        ? 'bg-success/12 text-success'
                        : 'bg-surface-2 text-content-muted'
                        }`}>
                        {selectedVehicle.status?.mainPowerOn ? 'Encendida' : 'Apagada'}
                      </span>
                    </div>
                    <div><strong>Batería:</strong> {selectedVehicle.status?.batteryLevel || 'N/A'}%</div>
                    <div><strong>Corte de combustible:</strong>
                      <span className={`ml-1 px-2 py-1 rounded text-xs ${selectedVehicle.status?.fuelCutOff
                        ? 'bg-error/12 text-error'
                        : 'bg-success/12 text-success'
                        }`}>
                        {selectedVehicle.status?.fuelCutOff ? 'Activado' : 'Desactivado'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Información adicional */}
                <div className="bg-surface-2 p-4 rounded-lg">
                  <h4 className="font-semibold text-content mb-2">Información Adicional</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-content-muted">
                    <div><strong>Kilometraje:</strong> {selectedVehicle.mileage ? (selectedVehicle.mileage / 1000).toFixed(1) + ' km' : 'N/A'}</div>
                    <div><strong>Odómetro:</strong> {selectedVehicle.odometer ? (selectedVehicle.odometer / 1000).toFixed(1) + ' km' : 'N/A'}</div>
                    <div><strong>Uso:</strong> {selectedVehicle.usage ? (selectedVehicle.usage / 1000).toFixed(1) + ' km' : 'N/A'}</div>
                  </div>
                </div>

                {/* Acciones */}
                <div className="bg-surface-2 p-4 rounded-lg">
                  <h4 className="font-semibold text-content mb-2">Acciones</h4>
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => {
                        const url = `https://www.google.com/maps?q=${selectedVehicle.latitude},${selectedVehicle.longitude}`;
                        window.open(url, '_blank');
                      }}
                      className="bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded text-sm"
                    >
                      📍 Ver en Google Maps
                    </button>
                    <button
                      onClick={() => {
                        const url = `https://waze.com/ul?ll=${selectedVehicle.latitude},${selectedVehicle.longitude}&navigate=yes`;
                        window.open(url, '_blank');
                      }}
                      className="bg-surface-2 hover:bg-surface text-content px-4 py-2 rounded text-sm"
                    >
                      🚗 Navegar con Waze
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquiposPage; 
